import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { getApiClient, publicClient } from '../../api/client';
import type { components } from '../../api/schema';
import { ExpectedApiError } from './errors';
import { buildListQueryParams, withDisabledFilter } from './query';

type CreateDistributorReportRequest = components['schemas']['CreateDistributorReportRequest'];
type DistributorCreate = components['schemas']['DistributorCreate'];
type DistributorUpdate = components['schemas']['DistributorUpdate'];

function getErrorDetail(error: unknown): string | undefined {
  return typeof error === 'object' &&
    error !== null &&
    'detail' in error &&
    typeof error.detail === 'string'
    ? error.detail
    : undefined;
}

export async function getDistributorByPath(path: string) {
  // Public endpoint - no auth needed. Used from the public distributor page.
  const { data, error, response } = await publicClient.GET('/distributors/path/{path}', {
    params: { path: { path } },
  });
  if (error) {
    const message = `Failed to fetch distributor: ${JSON.stringify(error)}`;
    if (response.status === 404) throw new ExpectedApiError(message);
    throw new Error(message);
  }
  return data;
}

export async function createDistributorReport(
  path: string,
  body: CreateDistributorReportRequest,
) {
  // Public endpoint - no auth needed. Used from the public distributor page.
  const { data, error, response } = await publicClient.POST('/distributors/path/{path}/create', {
    params: { path: { path } },
    body,
  });
  if (error) {
    if (response.status >= 400 && response.status < 500) {
      throw new ExpectedApiError(
        getErrorDetail(error) ?? `Failed to create distributor report: ${JSON.stringify(error)}`,
      );
    }
    throw new Error(`Failed to create distributor report: ${JSON.stringify(error)}`);
  }
  return data;
}

// --- Admin/Dashboard functions ---

export async function getDistributors(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
  textQuery?: string,
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query: withDisabledFilter(query),
    page, limit, range, now,
    rangeName: 'created_at',
    sortingField: sortingField ?? 'created_at',
    sortDirection: sortDirection ?? 'desc',
    textQuery,
  });
  const { data, error } = await client.GET('/distributors', {
    params: { query: params },
  });
  if (error) throw new Error(`Failed to fetch distributors: ${JSON.stringify(error)}`);
  return data;
}

export async function getDistributorsMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/distributors/meta');
  if (error) throw new Error(`Failed to fetch distributors meta: ${JSON.stringify(error)}`);
  return data;
}

export async function getDistributor(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/distributors/{uuid}', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to fetch distributor: ${JSON.stringify(error)}`);
  return data;
}

export async function getDistributorHierarchy(uuid: string) {
  const { data, error } = await getApiClient().GET('/distributors/{uuid}/hierarchy', {
    params: { path: { uuid } },
  });
  if (error) throw new Error('Unable to load this downline. Try again.');
  return data;
}

export async function updateDistributorNotification(
  uuid: string,
  descendantUuid: string,
  enabled: boolean,
) {
  const { data, error } = await getApiClient().PUT(
    '/distributors/{uuid}/notifications/{descendant_uuid}',
    { params: { path: { uuid, descendant_uuid: descendantUuid } }, body: { enabled } },
  );
  if (error || !data) throw new Error('Unable to save email preferences. Try again.');
  return data;
}

export async function createDistributor(body: DistributorCreate) {
  const client = getApiClient();
  const { data, error } = await client.POST('/distributors', { body });
  if (error) throw new Error(`Failed to create distributor: ${JSON.stringify(error)}`);
  return data;
}

export async function updateDistributor(uuid: string, body: DistributorUpdate) {
  const client = getApiClient();
  const { data, error } = await client.PUT('/distributors/{uuid}', { params: { path: { uuid } }, body });
  if (error) throw new Error(`Failed to update distributor: ${JSON.stringify(error)}`);
  return data;
}

export async function disableDistributor(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/distributors/{uuid}/disable', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to disable distributor: ${JSON.stringify(error)}`);
}

export async function enableDistributor(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/distributors/{uuid}/enable', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to enable distributor: ${JSON.stringify(error)}`);
}
